import { Record as record } from 'immutable';
import _ from 'lodash';

import { sum } from '../helpers/Immutable';
import { construct, resolve, fields } from '../helpers/LinkMapper';
import { getDefaults, getResolvers } from '../helpers/Records';
import Form from '../records/payment/Form';
import AuthRequest from '../records/payment/AuthRequest';
import AuthResponse from '../records/payment/AuthResponse';
import FeatureFlagStore from './FeatureFlagStore';
import BaseStore from './BaseStore';
import { ActionTypes, PaymentType } from '../constants/AppConstants';
import { PaymentUnauthorizedError } from '../errors/Payment';

export const getCompatibleCardPaymentMethod = ({ currentPaymentMethod, featurePay360ApiEnabled, featurePay360HostedEnabled }) => {
    let paymentMethod = PaymentType.PAY360_HOSTED;

    if (currentPaymentMethod === PaymentType.CARD && !featurePay360ApiEnabled && featurePay360HostedEnabled) {
        paymentMethod = PaymentType.PAY360_HOSTED;
    } else if (currentPaymentMethod === PaymentType.PAY360_HOSTED && !featurePay360HostedEnabled) {
        paymentMethod = PaymentType.CARD;
    } else {
        paymentMethod = currentPaymentMethod;
    }

    return paymentMethod;
};

const stateSchema = {
    form: [ resolve.as(Form) ],
    isProcessing: [fields.bool, false],
    is3DSecure: [fields.bool, false],
    authRequest: [resolve.as(AuthRequest)],
    authResponse: [resolve.as(AuthResponse)],
    authError: [fields.identity],
    errorMessage: [fields.identity],
    waitMessage: [fields.string],
    vouchersPreAuthedAmount: [fields.number, 0],
};
const defaultState = getDefaults(stateSchema);

class PaymentState extends record(defaultState) {
    static fromJSON(json = {}) {
        return construct(PaymentState, _.defaults(json, defaultState), getResolvers(stateSchema));
    }
}

class PaymentStore extends BaseStore {

    resetState() {
        // https://ontrackretail.slack.com/archives/C03KCFHMX/p1593180469005100
        this.setState(new PaymentState());
    }

    serialize() {
        // strip sensitive/unnecessary stuff
        return _.omit(super.serialize(), 'authRequest', 'authResponse');
    }

    getActionHandlers () {
        return {
            [ ActionTypes.PAYMENT_FORM_CHANGED ]: this.handlePaymentFormChanged,
            [ ActionTypes.PAYMENT_FORM_ERROR_MESSAGE ]: this.handlePaymentFormErrorMessage,
            [ ActionTypes.PAYMENT_DISCOUNT_CODE_CHANGED ]: this.handlePaymentDiscountCodeChanged,
            [ ActionTypes.PAYMENT_AUTH_STARTED ]: this.handlePaymentAuthStarted,
            [ ActionTypes.PAYMENT_AUTH_FINISHED ]: this.handlePaymentAuthFinished,
            [ ActionTypes.PAYMENT_AUTH_FAILED ]: this.handlePaymentAuthFailed,
            [ ActionTypes.PAYMENT_AUTH_RESET ]: this.handlePaymentAuthReset,
            [ ActionTypes.PAYMENT_AUTH_WAITING ]: this.handlePaymentWaiting,
            [ ActionTypes.ORDER_REFRESH_LOADED ]: this.handleOrderRefreshLoaded,
            [ ActionTypes.ORDER_REFRESH_FAILED ]: this.handleOrderRefreshFailed,
            [ ActionTypes.PAYMENT_LEFT ]: this.handlePaymentLeft,
            [ ActionTypes.ORDER_PAID ]: this.resetForm,
            [ ActionTypes.BASKET_CLEARED ]: this.resetState,
            [ ActionTypes.ORDER_EXPIRED ]: this.resetState,
            [ ActionTypes.SEARCH_STARTED ]: this.resetState,
            [ ActionTypes.SEASON_SEARCH_STARTED ]: this.resetState,
            [ ActionTypes.DAYSAVE_SEARCH_STARTED ]: this.resetState,
            [ ActionTypes.ROVERSRANGERS_SEARCH_STARTED ]: this.resetState,
            [ ActionTypes.AMAZON_PAY_INVALID_PAYMENT_METHOD ]: this.handleAmazonPayInvalidPaymentMethod,
            [ ActionTypes.CUSTOMER_VOUCHERS_LOADED ]: this.handleVouchersLoaded,
            [ ActionTypes.ORDER_UPDATED ]: this.handleOrderUpdated,
            [ ActionTypes.ORDER_TRIP_CREATED ]: this.handleOrderUpdated,
            [ ActionTypes.ORDER_TRIP_UPDATED ]: this.handleOrderUpdated,
            [ ActionTypes.ORDER_TRIP_DELETED ]: this.handleOrderUpdated,
            [ ActionTypes.PAYMENT_PRE_AUTH_VOUCHERS_RELEASED ]: this.handleVoucherPreAuthRelease,
            [ ActionTypes.PAYMENT_PRE_AUTH_VOUCHERS_COMPLETED ]: this.handleVoucherPreAuthComplete,
            [ ActionTypes.LOGGED_OUT ]: this.resetForm,
            [ ActionTypes.LOGGED_IN ]: this.handleLogin,
            [ ActionTypes.APPLY_DISCOUNT_CODE_FINISHED]: this.handleDiscountApplied,

        };
    }

    handleLogin ({ user }) {
        const { form } = this.state;
        if (form) {
            this.setState(
                this.state.set(
                    'form',
                    form.set('isLoggedIn', true).setContactDetails(user.email, user.phone),
                ),
            );
        }
    }

    handleVouchersLoaded ({ vouchers }) {
        if (!FeatureFlagStore.get('enableEVoucherCheckout')) {
            return;
        }

        const total = sum(vouchers, 'amount');
        const { form, vouchersPreAuthedAmount } = this.state;
        if (form && form.getVouchersTotal() !== total + vouchersPreAuthedAmount) {
            this.setState(this.state.set('form', form.setVouchersTotal(total, vouchersPreAuthedAmount)));
        }
    }

    handleOrderUpdated ({ order }) {
        const { form } = this.state;
        if (form && form.basketTotal !== order.totalPrice) {
            this.setState(this.state.set('form', form.setBasketTotal(order.totalPrice)));
        }
    }

    handleDiscountApplied ({ updatedOrder }) {
        this.handleOrderUpdated({ order: updatedOrder });
    }

    handlePaymentFormErrorMessage({ errorMessage }) {
        this.setState(this.state.set('errorMessage', errorMessage));
    }

    handlePaymentFormChanged ({ form }) {
        this.setState(this.state.set('form', form));
    }

    handlePaymentDiscountCodeChanged ({ discountCode }) {
        this.setState(this.state.setIn(['form', 'discountCode'], discountCode));
    }

    handlePaymentAuthStarted () {
        const updatedState = this.state.withMutations((state) => {
            return state
                .set('isProcessing', true)
                .set('is3DSecure', false)
                .set('authRequest', null)
                .set('authResponse', null)
                .set('authError', null);
        });
        this.setState(updatedState);
    }

    handlePaymentAuthFinished ({ authRequest, authResponse }) {
        const updatedState = this.state.withMutations((state) => {
            return state
                .set('isProcessing', authResponse.isEnrolled())
                .set('is3DSecure', authResponse.isEnrolled())
                .set('authRequest', authRequest)
                .set('authResponse', authResponse)
                .set('authError', null);
        });
        this.setState(updatedState);
    }

    handlePaymentAuthFailed ({ authRequest, authError }) {
        const updatedState = this.state.withMutations((state) => {
            return state
                .set('isProcessing', false)
                .set('is3DSecure', false)
                .set('authRequest', authRequest)
                .set('authResponse', null)
                .set('authError', authError)
                .set('waitMessage', null);
        });
        this.setState(updatedState);
    }

    handlePaymentLeft() {
        this.resetState();
    }

    handlePaymentWaiting ({ message }) {
        this.setState(this.state.set('waitMessage', message));
    }

    handleOrderRefreshLoaded ({ order }) {
        const updatedState = this.state.withMutations((state) => {
            return state
                .set('isProcessing', false)
                .set('is3DSecure', false)
                .set('authError', !order.isPaid() && new PaymentUnauthorizedError(order.state))
                .set('waitMessage', null);
        });
        this.setState(updatedState);
    }

    handleOrderRefreshFailed ({ error }) {
        const updatedState = this.state.withMutations((state) => {
            return state
                .set('isProcessing', false)
                .set('is3DSecure', false)
                .set('authError', new PaymentUnauthorizedError(error.message))
                .set('waitMessage', null);
        });
        this.setState(updatedState);
    }

    handlePaymentAuthReset () {
        const updatedState = this.state.withMutations((state) => {
            return state
                .set('isProcessing', false)
                .set('is3DSecure', false)
                .set('authRequest', null)
                .set('authResponse', null)
                .set('authError', null)
                .set('waitMessage', null);
        });
        this.setState(updatedState);
    }

    handleAmazonPayInvalidPaymentMethod() {
        this.setState(this.state.merge({
            isProcessing: false
        }));
    }

    handleVoucherPreAuthRelease() {
        this.setState(this.state.set('vouchersPreAuthedAmount', 0));
    }

    handleVoucherPreAuthComplete({amount}) {
        this.setState(this.state.set('vouchersPreAuthedAmount', amount));
    }

    resetForm () {
        const updatedState = this.state.set('form', defaultState.form);
        this.setState(updatedState);
    }

    getForm () {
        return this.get('form');
    }

    getValidation () {
        return this.get('validation');
    }

    isProcessing () {
        return this.get('isProcessing');
    }

    is3DSecure () {
        return this.get('is3DSecure');
    }

    getAuthRequest () {
        return this.get('authRequest');
    }

    getAuthResponse () {
        return this.get('authResponse');
    }

    getAuthError () {
        return this.get('authError');
    }

    getPaymentAmount () {
        if (!this.state.form) {
            return 0;
        }
        return this.state.form.getPaymentAmount();
    }
}


const storeInstance = new PaymentStore({
    storageKey: 'payment',
    expires: 30 * 60,
    recordType: PaymentState,
});
export default storeInstance;



// WEBPACK FOOTER //
// ./src/stores/PaymentStore.js